import React, { forwardRef } from 'react';
import { Button, Modal, Tooltip } from 'antd';
import type { ButtonProps } from 'antd';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isSubscriptionExpired, RENEW_ROUTE } from '../utils/subscriptionGuard';
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '../actions/constants/subscriptionConstants';

const getRole = (): string | undefined => {
  try {
    const auth = localStorage.getItem('AUTHDATA');
    return auth ? JSON.parse(auth).role : undefined;
  } catch {
    return undefined;
  }
};


export const useSubscriptionGuard = () => {
  const navigate = useNavigate();
  const subscriptionStatus = useSelector((state: any) => state.userSubscriptionStatusReducer);
  const isReadOnly = isSubscriptionExpired(subscriptionStatus, getRole());

  const promptRenew = () => {
    Modal.confirm({
      title: 'Subscription expired',
      content: SUBSCRIPTION_EXPIRED_MESSAGE,
      okText: 'Renew now',
      cancelText: 'Close',
      centered: true,
      onOk: () => navigate(RENEW_ROUTE),
    });
  };

  const guard = <E,>(handler?: (e: E) => void) => (e: E) => {
    if (isReadOnly) {
      (e as any)?.preventDefault?.();
      (e as any)?.stopPropagation?.();
      promptRenew();
      return;
    }
    handler?.(e);
  };

  return { isReadOnly, promptRenew, guard };
};

export interface GuardedButtonProps extends ButtonProps {
  readOnlyTooltip?: string;
}


const GuardedButton = forwardRef<HTMLButtonElement, GuardedButtonProps>(
  ({ onClick, className, style, readOnlyTooltip = 'Renew your subscription to continue', ...rest }, ref) => {
    const { isReadOnly, guard } = useSubscriptionGuard();

    const button = (
      <Button {...rest} ref={ref} onClick={guard(onClick as (e: React.MouseEvent<HTMLElement>) => void)}
        className={`${className ?? ''}${isReadOnly ? ' guarded-readonly' : ''}`.trim() || undefined}
        style={isReadOnly ? { ...style, opacity: 0.6 } : style} />
    );

    return isReadOnly ? <Tooltip title={readOnlyTooltip}>{button}</Tooltip> : button;
  },
);

GuardedButton.displayName = 'GuardedButton';

export default GuardedButton;
